import { useSearchUsersQuery, type User } from '@paintscout/api'
import React, { createContext, useContext, useMemo, useState } from 'react'
import { useUser } from '../UserProvider'
import { useClientOptions } from '../ClientOptionsProvider'
import { removeAuth0Prefix } from '@paintscout/util'

interface UsersContextValue {
  users?: User[]
  userPictures?: Record<string, string>
  loading?: boolean
  refetchUsers?: () => void
}

export const UsersContext = createContext<UsersContextValue | undefined>(undefined)

export const UsersProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([])
  const { clientId } = useClientOptions()
  const { isSuperadmin } = useUser()
  const { user } = useUser()

  const usersQuery = isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : ''

  const { loading, refetch } = useSearchUsersQuery({
    variables: {
      query: usersQuery,
      includeInactive: true
    },
    skip: !user,
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      setUsers(data?.searchUsers?.rows ?? [])
    }
  })

  return (
    <UsersContext.Provider
      value={{
        loading,
        users,
        userPictures: useMemo(
          () =>
            users?.reduce(
              (acc, user) => {
                acc[user.user_id] = user.user_metadata?.customAvatar?.cdnValue
                acc[removeAuth0Prefix(user.user_id)] = user.user_metadata?.customAvatar?.cdnValue

                return acc
              },
              {} as Record<string, string>
            ),
          [users]
        ),
        refetchUsers: refetch
      }}
    >
      {children}
    </UsersContext.Provider>
  )
}

export const useUsers = (): UsersContextValue => {
  const context = useContext(UsersContext)
  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider')
  }
  return context
}
